import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight } from 'lucide-react';
import { API } from '../App';
import { devError } from '../utils/logger';

/**
 * SimilarListings — slim carousel of "more like this" cards rendered at
 * the bottom of every listing detail page.
 *
 * Powered by `GET /api/listings/{id}/similar` which returns up to 6 listings
 * in the same category (preferring matching condition). Stays silent if the
 * API returns nothing — empty state for "we don't have anything similar yet"
 * adds clutter rather than value.
 */
export default function SimilarListings({ listingId, limit = 6 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!listingId) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/listings/${listingId}/similar`, { params: { limit } })
      .then((res) => { if (!cancelled) setItems(res.data?.listings || []); })
      .catch((err) => {
        devError('similar listings load failed', err);
        if (!cancelled) setItems([]);
      });
    return () => { cancelled = true; };
  }, [listingId, limit]);

  if (!items || items.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-border/30" data-testid="similar-listings">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Find similar
          </p>
          <h2 className="font-playfair text-2xl md:text-3xl">More like this</h2>
        </div>
        <Link to="/marketplace" className="text-sm text-muted-foreground hover:text-accent inline-flex items-center gap-1">
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.map((l) => (
          <Link
            key={l.listing_id}
            to={`/listing/${l.listing_id}`}
            className="group rounded-xl overflow-hidden border border-border/30 bg-card/40 hover:border-accent/40 transition-colors"
            data-testid={`similar-card-${l.listing_id}`}
          >
            <div
              className="aspect-[4/3] bg-secondary/40 bg-cover bg-center"
              style={l.image_url ? { backgroundImage: `url(${l.image_url})` } : {}}
            />
            <div className="p-3">
              <p className="text-sm font-medium text-foreground line-clamp-2 group-hover:text-accent transition-colors">
                {l.title}
              </p>
              <p className="text-xs text-muted-foreground mt-1 capitalize">
                {l.category}{l.condition ? ` · ${l.condition}` : ''}
              </p>
              {l.price != null && (
                <p className="text-sm text-accent font-mono mt-2">
                  ${Number(l.price).toLocaleString()}
                  <span className="text-xs text-muted-foreground">/{l.unit || 'piece'}</span>
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
